import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Skeleton, Text } from '@mantine/core';
import AttentionList from '@/components/app/home/AttentionList';
import HomeSection from '@/components/app/home/HomeSection';
import type { AttentionView } from '@/lib/dashboard';
import { ROUTES } from '@/routes/paths';

interface AttentionSectionProps {
  readonly view: AttentionView;
  readonly loading: boolean;
  /** Сделки не загрузились совсем. */
  readonly failed: boolean;
  /** Загрузилась только часть данных: сделки одной стороны или уведомления о приостановках. */
  readonly partial: boolean;
  /** Есть сделки, поэтому при пустой ленте можно сказать, что срочных дел нет. */
  readonly hasDeals: boolean;
  readonly retrying: boolean;
  readonly onRetry: () => void;
}

// Блок «Требует вашего внимания». При сбое честно сообщает о нём: фраза «срочных дел нет» допустима
// только при полных данных, иначе человек пропустит подписание или приостановку.
export default function AttentionSection({ view, loading, failed, partial, hasDeals, retrying, onRetry }: AttentionSectionProps) {
  const { t } = useTranslation();
  const title = t('app.home.attention.title');
  const hasItems = view.mine.length + view.waiting.length > 0;

  if (loading) {
    return (
      <HomeSection title={title}>
        <Skeleton h={200} mt="md" aria-busy />
      </HomeSection>
    );
  }

  if (!failed && !partial && !hasItems && !hasDeals) {
    return null;
  }

  return (
    <HomeSection title={title} link={hasItems ? { to: ROUTES.APP_DEALS, label: t('app.home.attention.all') } : undefined}>
      {failed || partial ? (
        <Alert color="red" variant="light" mt="md">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" style={{ flex: '1 1 200px' }}>
              {t(failed ? 'app.home.attention.loadError' : 'app.home.attention.partialError')}
            </Text>
            <Button variant="subtle" size="compact-sm" loading={retrying} onClick={onRetry}>
              {t('common.retry')}
            </Button>
          </Group>
        </Alert>
      ) : null}
      {hasItems ? <AttentionList view={view} /> : null}
      {!hasItems && !failed && !partial ? (
        <Text c="gray.7" size="sm" role="status" mt="md">
          {t('app.home.attention.empty')}
        </Text>
      ) : null}
    </HomeSection>
  );
}
